import React from "react";
import { STAGE_LABELS } from "@/lib/api";
import { AlertTriangle, ChevronRight, Copy, Check, Hourglass } from "lucide-react";

/**
 * Single draggable job card on the Kanban board.
 * Extracted from Jobs.jsx so the parent component stays under the Babel AST limit
 * and so the card can be tested / styled in isolation.
 */
export default function JobCard({
  job,
  stage,
  boardStages,
  priorityStyles,
  isSelected,
  isDragging,
  isOverdue,
  isStale,                 // boolean — > priority threshold days in current stage
  staleDaysInStage,        // float — actual days for the badge label
  clientLabel,
  onSelect,
  onToggleSelect,
  onAdvance,
  onDuplicate,
  onDragStart,
  onDragEnd,
}) {
  const pri = priorityStyles[job.priority || "normal"];
  const Icon = pri.icon;
  const sa = job.stage_assignees || {};
  const currentAssignee = sa[stage];
  const nextStageIdx = boardStages.indexOf(stage) + 1;
  const nextStage = nextStageIdx < boardStages.length ? boardStages[nextStageIdx] : null;
  const nextAssignee = nextStage ? sa[nextStage] : null;

  return (
    <div
      onClick={() => onSelect(job)}
      data-testid={`job-card-${job.id}`}
      draggable
      onDragStart={(e) => onDragStart(e, job.id)}
      onDragEnd={onDragEnd}
      className={`border bg-white p-3 cursor-grab active:cursor-grabbing hover:shadow-brut transition-all relative ${
        isSelected ? "border-blue-600 border-2" : "border-zinc-900"
      } ${isOverdue ? "ring-1 ring-red-600" : ""} ${isStale && !isOverdue ? "ring-1 ring-orange-500" : ""} ${isDragging ? "opacity-40" : ""}`}
    >
      <div className="flex items-start justify-between gap-2 mb-1">
        <div className="flex items-center gap-1 flex-1 min-w-0">
          <div className="font-mono text-[10px] text-zinc-500 truncate">{job.job_number}</div>
          {(() => {
            const cat = job.category || "live";
            if (cat === "live") return null;
            const cls = cat === "practice" ? "bg-blue-100" : "bg-purple-100";
            return (
              <span className={`text-[8px] font-mono font-bold px-1 py-0.5 border border-zinc-900 ${cls} uppercase`} data-testid={`cat-${job.id}`}>
                {cat}
              </span>
            );
          })()}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {(job.priority === "urgent" || job.priority === "high") && Icon && (
            <span className={`flex items-center gap-0.5 text-[9px] font-mono font-bold px-1 py-0.5 border border-zinc-900 ${pri.bg}`}>
              <Icon className="w-2.5 h-2.5" /> {pri.label}
            </span>
          )}
          <button
            onClick={(e) => { e.stopPropagation(); onToggleSelect(job.id); }}
            className={`w-4 h-4 border border-zinc-900 flex items-center justify-center ${
              isSelected ? "bg-blue-600" : "bg-white hover:bg-zinc-100"
            }`}
            data-testid={`select-${job.id}`}
            title="Select for bulk actions"
          >
            {isSelected && <Check className="w-3 h-3 text-white" />}
          </button>
        </div>
      </div>

      <div className="font-bold text-sm leading-tight">{job.project_name}</div>
      {clientLabel && (
        <div className="text-xs text-zinc-600 mt-1" data-testid={`job-client-${job.id}`}>
          {clientLabel}
        </div>
      )}
      {job.team && (
        <span className="inline-block text-[9px] font-mono mt-1 px-1 py-0.5 bg-zinc-100 border border-zinc-300">
          {job.team}
        </span>
      )}
      {currentAssignee && (
        <div className="mt-1.5 text-[10px] font-mono flex items-center gap-1 bg-zinc-50 border border-zinc-200 px-1.5 py-0.5">
          <span className="text-zinc-500">{STAGE_LABELS[stage].toUpperCase()}</span>
          <span className="font-bold truncate">· {currentAssignee}</span>
        </div>
      )}
      {isOverdue && (
        <div className="mt-1 flex items-center gap-1 text-[10px] font-mono font-bold text-red-600">
          <AlertTriangle className="w-3 h-3" /> OVERDUE · DUE {job.due_date}
        </div>
      )}
      {isStale && !isOverdue && (
        <div
          className="mt-1 flex items-center gap-1 text-[10px] font-mono font-bold text-orange-700"
          data-testid={`stale-badge-${job.id}`}
          title={`In ${STAGE_LABELS[stage]} for ${staleDaysInStage}d — exceeds threshold for ${job.priority || "normal"} priority`}
        >
          <Hourglass className="w-3 h-3" /> STUCK · {staleDaysInStage}d IN STAGE
        </div>
      )}

      <div className="flex items-center justify-between mt-2 text-[10px] font-mono">
        <span>EFF: <b>{job.efficiency || 0}%</b></span>
        <span>{job.taken_time_hours || 0}h / {job.given_time_hours || 0}h</span>
      </div>

      <div className="flex gap-1 mt-2">
        {nextStage && (
          <button
            onClick={(e) => { e.stopPropagation(); onAdvance(job, nextStage); }}
            data-testid={`advance-${job.id}`}
            className="flex-1 text-[10px] font-mono font-bold bg-zinc-900 text-white px-2 py-1 hover:bg-zinc-700 flex items-center justify-center gap-1 truncate"
            title={nextAssignee ? `→ ${STAGE_LABELS[nextStage]} · ${nextAssignee}` : `→ ${STAGE_LABELS[nextStage]}`}
          >
            → {STAGE_LABELS[nextStage].toUpperCase()}
            {nextAssignee ? ` · ${nextAssignee.split(" ")[0]}` : ""} <ChevronRight className="w-3 h-3" />
          </button>
        )}
        <select
          value=""
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            const target = e.target.value;
            e.target.value = "";
            if (target && target !== stage) onAdvance(job, target);
          }}
          data-testid={`move-to-${job.id}`}
          title="Move to any stage (mobile-friendly)"
          className="text-[10px] font-mono font-bold border border-zinc-900 bg-yellow-100 px-1 py-1 hover:bg-yellow-200"
        >
          <option value="">⇄</option>
          {boardStages.filter((bs) => bs !== stage).map((bs) => (
            <option key={bs} value={bs}>→ {STAGE_LABELS[bs]}</option>
          ))}
        </select>
        <button
          onClick={(e) => { e.stopPropagation(); onDuplicate(job); }}
          title="Duplicate job"
          data-testid={`duplicate-${job.id}`}
          className="text-[10px] font-mono font-bold border border-zinc-900 bg-white px-2 py-1 hover:bg-zinc-100"
        >
          <Copy className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
}
